import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next"
import { Controller } from "react-hook-form"
import FormSimple from "@components/formGroup/formSimple"
import { Input } from "@components/tailwindComponents"
import {
  REGEX_EMAIL,
  REGEX_PHONE,
  REGEX_PHONE_INVERSE,
  REGEX_POST_NUMBER
} from "@helpers/_constants";
import { convertToNumericString } from "@helpers/_functions";

export const useSectionEmailAndPhoneColumns = () => {
  const { t } = useTranslation("pages.employés.infoPers")

  return useMemo(() => [
    { id: "personalEmail", label: t("Courriel de correspondance*") },
    { id: "professionalEmail", label: t("Autre courriel") },
    { id: "professionalPhone", label: t("Téléphone Professionnel") },
    { id: "professionalPhonePostNumber", label: t("N de poste") },
    { id: "personalPhone", label: t("Téléphone personnel") },
    { id: "personalPhonePostNumber", label: t("N de poste") }
  ], [t])
}

export const SectionEmailAndPhoneTableBulkHeader = ({ hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers")

  return (
    <>
      <th
        key="personalEmail"
        className={`arh-w-[15.5rem] arh-min-w-[15.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.personalEmail ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Courriel de correspondance*")}
      </th>
      <th
        key="professionalEmail"
        className={`arh-w-[14.5rem] arh-min-w-[14.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.professionalEmail ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Autre courriel")}
      </th>
      <th
        key="professionalPhone"
        className={`arh-w-[8.75rem] arh-min-w-[11.75rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.professionalPhone ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Téléphone Professionnel")}
      </th>
      <th
        key="professionalPhonePostNumber"
        className={`arh-w-[7.25rem] arh-min-w-[7.25rem] arh-max-w-[7.25rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.professionalPhonePostNumber ? "arh-hidden" : ""}`}
      >
        {mainTranslator("N de poste")}
      </th>
      <th
        key="personalPhone"
        className={`arh-w-[11.75rem] arh-min-w-[11.75rem] arh-max-w-[11.75rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.personalPhone ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Téléphone personnel")}
      </th>
      <th
        key="personalPhonePostNumber"
        className={`arh-w-[7.25rem] arh-min-w-[7.25rem] arh-max-w-[7.25rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.personalPhonePostNumber ? "arh-hidden" : ""}`}
      >
        {mainTranslator("N de poste")}
      </th>
    </>
  )
}

export const SectionEmailAndPhoneTableBulkCells = ({ control, employeeIndex, employee, readOnly = false, mustWait, getValues, hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers")

  const makeEmailRule = useCallback(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;
          
          return value === "" || REGEX_EMAIL.test(value);
        }
      }
    };
  }, [getValues, employeeIndex]);

  const makePhoneRule = useCallback(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;
          
          return value === "" || REGEX_PHONE.test(value);
        }
      }
    };
  }, [getValues, employeeIndex]);

  const makePostNumberRule = useCallback(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;
          
          return value === "" || REGEX_POST_NUMBER.test(value);
        }
      }
    };
  }, [getValues, employeeIndex]);

  return (
    <>
      <td
        key="personalEmail"
        className={`arh-w-[15.5rem] arh-min-w-[15.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.personalEmail ? "arh-hidden" : ""}`}
      >
          <Controller
            control={control}
            name={`employees.${employeeIndex}.emails.personalEmail`}
            defaultValue={employee.emails?.personalEmail || ""}
            rules={makeEmailRule()}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <FormSimple
                placeholder={mainTranslator("Courriel de correspondance*")}
                type="email"
                onChange={(e) => onChange(e.target.value.toLowerCase())}
                value={value}
                readOnly={mustWait || readOnly}
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                edit={true}
                invalid={invalid}
              />
            )}
          />
      </td>
      <td
        key="professionalEmail"
        className={`arh-w-[14.5rem] arh-min-w-[14.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.professionalEmail ? "arh-hidden" : ""}`}
      >
          <Controller
            control={control}
            name={`employees.${employeeIndex}.emails.professionalEmail`}
            defaultValue={employee.emails?.professionalEmail || ""}
            rules={makeEmailRule()}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <FormSimple
                placeholder={mainTranslator("Autre courriel")}
                type="email"
                onChange={(e) => onChange(e.target.value.toLowerCase())}
                value={value}
                readOnly={mustWait || readOnly}
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                edit={true}
                invalid={invalid}
              />
            )}
          />
      </td>
      <td
        key="professionalPhone"
        className={`arh-w-[8.75rem] arh-min-w-[11.75rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.professionalPhone ? "arh-hidden" : ""}`}
      >
          <Controller
            control={control}
            name={`employees.${employeeIndex}.emails.professionalPhone`}
            defaultValue={employee.emails?.professionalPhone || ""}
            rules={makePhoneRule()}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <FormSimple
                placeholder={mainTranslator("00 00 00 00 00")}
                type="text"
                onChange={(e) => {
                  const cleanedValue = e.target.value.replace(
                    REGEX_PHONE_INVERSE,
                    ""
                  );
                  onChange(cleanedValue);
                }}
                value={value}
                readOnly={mustWait || readOnly}
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                edit={true}
                invalid={invalid}
              />
            )}
          />
      </td>
      <td
        key="professionalPhonePostNumber"
        className={`arh-w-[7.25rem] arh-min-w-[7.25rem] arh-max-w-[7.25rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.professionalPhonePostNumber ? "arh-hidden" : ""}`}
      >
          <Controller
            control={control}
            name={`employees.${employeeIndex}.emails.professionalPhonePostNumber`}
            defaultValue={employee.emails?.professionalPhonePostNumber || ""}
            rules={makePostNumberRule()}
            render={({ field: { onChange, value, name }, fieldState: { invalid } }) => (
              <Input
                value={value}
                onChange={(e) => {
                  onChange(convertToNumericString(e.target.value));
                }}
                name={name}
                placeholder="000"
                disabled={mustWait || readOnly}
                additionnalClas="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                edit={true}
                invalid={invalid}
              />
            )}
          />
      </td>
      <td
        key="personalPhone"
        className={`arh-w-[11.75rem] arh-min-w-[11.75rem] arh-max-w-[11.75rem]  arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.personalPhone ? "arh-hidden" : ""}`}
      >
          <Controller
            control={control}
            name={`employees.${employeeIndex}.emails.personalPhone`}
            defaultValue={employee.emails?.personalPhone || ""}
            rules={makePhoneRule()}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <FormSimple
                placeholder={mainTranslator("00 00 00 00 00")}
                type="text"
                onChange={(e) => {
                  const cleanedValue = e.target.value.replace(
                    REGEX_PHONE_INVERSE,
                    ""
                  );
                  onChange(cleanedValue);
                }}
                value={value}
                readOnly={mustWait || readOnly}
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                edit={true}
                invalid={invalid}
              />
            )}
          />
      </td>
      <td
        key="personalPhonePostNumber"
        className={`arh-w-[7.25rem] arh-min-w-[7.25rem] arh-max-w-[7.25rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.personalPhonePostNumber ? "arh-hidden" : ""}`}
      >
          <Controller
            control={control}
            name={`employees.${employeeIndex}.emails.personalPhonePostNumber`}
            defaultValue={employee.emails?.personalPhonePostNumber || ""}
            rules={makePostNumberRule()}
            render={({ field: { onChange, value, name }, fieldState: { invalid } }) => (
              <Input
                value={value}
                onChange={(e) => {
                  onChange(convertToNumericString(e.target.value));
                }}
                name={name}
                placeholder="000"
                disabled={mustWait || readOnly}
                additionnalClas="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                edit={true}
                invalid={invalid}
              />
            )}
          />
      </td>
    </>
  )
}

export const getEmailAndPhoneColumnCount = () => 6